import { Formik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import {
  Button,
  Buttons,
  CancelButton,
  DeleteButton,
  DeleteConfirmation,
  Drawer,
  Field,
  Form,
  FormMessage,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import { ErrorPage } from '~/routes/public/pages';
import { emptyStringToNull, mergeValues } from '~/utils';

export default function AssistantProfileForm({ profileId, onDeleted, onSaved, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ status, message, isSubmitting }, form] = useForm();
  const toast = useToast();
  const [query, setQuery] = useState({ isReady: false, data: null });

  const fetchData = useCallback(async () => {
    if (!profileId) {
      setQuery({ isReady: true, data: {} });
      return;
    }

    try {
      const { data } = await api.www.workspaces(workspace.id).ai().assistantProfiles(profileId).get();
      setQuery({ isReady: true, data });
    } catch (error) {
      toast.error(error.message || 'There was a problem reading this profile. Please try again.');
    }
  }, [workspace.id, profileId, api, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  const isNew = !profileId;
  const title = isNew ? 'New Assistant Profile' : 'Edit Assistant Profile';

  useDocumentTitle(title);

  const confirmation = useConfirmation();
  const handleDelete = async () => {
    await confirmation.prompt((resolve) => (
      <DeleteConfirmation
        resolve={async (confirm) => {
          if (!confirm) {
            resolve(false);
            return;
          }
          await api.www.workspaces(workspace.id).ai().assistantProfiles(profileId).remove();
          await onDeleted?.();
          resolve(true);
          toast.success('Assistant profile deleted.');
        }}>
        Are you sure you want to delete this assistant profile?
      </DeleteConfirmation>
    ));
  };

  if (!query.isReady) return null;
  if (!query.data) return <ErrorPage.NotFound publicSite={false} />;

  const canManage = isNew || query.data.permissions?.manage;
  const isSystem = query.data.scopeKey === 'system';

  const initialValues = mergeValues(
    {
      name: '',
      description: '',
      instructions: '',
      scopeKey: 'workspace',
    },
    query.data,
  );

  const validationSchema = Yup.object().shape({
    name: Yup.string().max(255).label('Name').required(),
    description: Yup.string().label('Description').max(5000),
    instructions: Yup.string().label('Instructions').required(),
  });

  return (
    <Drawer
      isOpen
      title={title}
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            form.submit();
            const body = emptyStringToNull({
              name: values.name,
              description: values.description,
              instructions: values.instructions,
            });

            if (isNew) {
              body.scopeKey = values.scopeKey;
            }

            if (profileId) {
              await api.www.workspaces(workspace.id).ai().assistantProfiles(profileId).update(body);
            } else {
              await api.www.workspaces(workspace.id).ai().assistantProfiles().add(body);
            }

            await onSaved?.();
            form.save();
            closeDrawer();
            toast.success(isNew ? 'Assistant profile created.' : 'Assistant profile saved.');
          } catch ({ message }) {
            form.error({ message });
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={validationSchema}>
            {(formik) => {
              return (
                <Form>
                  <Form.Section title="Assistant Profile">
                    <Form.Control>
                      <Field.Text name="name" placeholder="Name" maxLength={255} autoFocus disabled={isSystem} />
                    </Form.Control>

                    <Form.Control>
                      <Field.TextArea
                        name="description"
                        placeholder="Description"
                        maxLength={5000}
                        disabled={isSystem}
                      />
                    </Form.Control>

                    <Form.Control>
                      <Field.TextArea
                        name="instructions"
                        placeholder="Instructions"
                        style={{ minHeight: '12rem' }}
                        disabled={isSystem}
                      />
                    </Form.Control>
                  </Form.Section>

                  {status && <FormMessage.Error>{message}</FormMessage.Error>}

                  <Drawer.Actions>
                    {!isNew && !isSystem && canManage && <DeleteButton onClick={handleDelete}>Delete</DeleteButton>}

                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                      {canManage && !isSystem && (
                        <Button isLoading={isSubmitting} onClick={formik.submitForm}>
                          Save &amp; Close
                        </Button>
                      )}
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}
